import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert, CircularProgress, Grid, Switch, FormControlLabel
} from '@mui/material';
import api from '../../services/api';

/**
 * Admin → Sign-in Sessions (security hardening batch 2, 30 Sep 2026).
 *
 * How long a sign-in lasts. The idle timeout ends a session after a period with no
 * activity (a live call, voice session or waiting room counts as activity); the
 * absolute limit ends it however active the person is. Staff are admins, therapists,
 * clinical supervisors, researchers and training roles; everyone else is a client.
 * Changes apply to new sign-ins.
 */

const DEFAULTS = {
  enforceIdleTimeout: true,
  staffIdleMinutes: 15,
  clientIdleMinutes: 30,
  staffAbsoluteHours: 12,
  clientAbsoluteDays: 7,
  mobileIdleMinutes: 60,
  mobileAbsoluteDays: 30
};

const FIELDS = [
  { key: 'staffIdleMinutes', label: 'Staff: sign out after inactivity (minutes)', min: 5, max: 240 },
  { key: 'staffAbsoluteHours', label: 'Staff: longest sign-in (hours)', min: 1, max: 72 },
  { key: 'clientIdleMinutes', label: 'Clients: sign out after inactivity (minutes)', min: 5, max: 1440 },
  { key: 'clientAbsoluteDays', label: 'Clients: longest sign-in (days)', min: 1, max: 30 },
  { key: 'mobileIdleMinutes', label: 'Mobile app: sign out after inactivity (minutes)', min: 5, max: 1440 },
  { key: 'mobileAbsoluteDays', label: 'Mobile app: longest sign-in (days)', min: 1, max: 90 }
];

// Roles that must use two-step sign-in (security batch 4).
const MFA_ROLE_OPTIONS = [
  { role: 'admin', label: 'Admins' },
  { role: 'clinical_supervisor', label: 'Clinical supervisors' },
  { role: 'therapist', label: 'Therapists' },
  { role: 'researcher', label: 'Researchers' }
];

// "Trust this device" (1 Oct 2026): days a device may skip the two-step code.
const TRUST_DEFAULTS = {
  trustDevicesEnabled: true,
  privilegedRoles: ['admin', 'clinical_supervisor'],
  privilegedTrustDays: 7,
  standardTrustDays: 30,
  newDeviceEmails: true
};
const TRUST_FIELDS = [
  { key: 'privilegedTrustDays', label: 'Admins and clinical supervisors: trust a device for (days)', min: 1, max: 30 },
  { key: 'standardTrustDays', label: 'Everyone else: trust a device for (days)', min: 1, max: 90 }
];

const SessionSecuritySettings = () => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [mfaRoles, setMfaRoles] = useState(['admin']);
  const [trust, setTrust] = useState(TRUST_DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const platform = await api.getPlatformSettings();
        setSettings({ ...DEFAULTS, ...(platform?.sessionSettings || {}) });
        if (Array.isArray(platform?.mfaSettings?.requiredRoles)) setMfaRoles(platform.mfaSettings.requiredRoles);
        const m = platform?.mfaSettings || {};
        setTrust(t => ({ ...t, ...Object.fromEntries(Object.keys(TRUST_DEFAULTS).filter(k => m[k] !== undefined).map(k => [k, m[k]])) }));
      } catch (err) {
        setError(err.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const outOfRange = (value, f) => { const v = Number(value); return !Number.isFinite(v) || v < f.min || v > f.max; };
  const invalid = FIELDS.find(f => outOfRange(settings[f.key], f)) || TRUST_FIELDS.find(f => outOfRange(trust[f.key], f));

  const save = async () => {
    setSaving(true); setError(null); setSuccess(false);
    try {
      const clean = { ...settings };
      FIELDS.forEach(f => { clean[f.key] = Number(settings[f.key]); });
      const trustClean = { ...trust };
      TRUST_FIELDS.forEach(f => { trustClean[f.key] = Number(trust[f.key]); });
      await api.updatePlatformSettings({ sessionSettings: clean, mfaSettings: { requiredRoles: mfaRoles, ...trustClean } });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Sign-in Sessions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How long a sign-in lasts. After the inactivity limit the person is warned two minutes
        before being signed out; a live video call, voice session or waiting room keeps them
        signed in. Changes apply to new sign-ins.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Settings saved.</Alert>}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <FormControlLabel
            control={<Switch id="session-enforce-idle" checked={!!settings.enforceIdleTimeout}
              onChange={e => setSettings(s => ({ ...s, enforceIdleTimeout: e.target.checked }))} />}
            label="Sign people out after inactivity"
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {FIELDS.map(f => (
              <Grid item xs={12} md={6} key={f.key}>
                <TextField
                  id={`session-${f.key}`}
                  fullWidth type="number" label={f.label}
                  value={settings[f.key]}
                  onChange={e => setSettings(s => ({ ...s, [f.key]: e.target.value }))}
                  inputProps={{ min: f.min, max: f.max }}
                  helperText={`${f.min} to ${f.max}`}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Two-step sign-in</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            People in these roles must add a code from an authenticator app to every sign-in. Anyone
            without it set up is asked to set it up at their next sign-in and can do nothing else until
            they have. Other people can turn it on themselves from Change Password.
          </Typography>
          {MFA_ROLE_OPTIONS.map(o => (
            <FormControlLabel
              key={o.role}
              control={<Switch id={`mfa-role-${o.role}`} checked={mfaRoles.includes(o.role)}
                onChange={e => setMfaRoles(r => e.target.checked ? [...new Set([...r, o.role])] : r.filter(x => x !== o.role))} />}
              label={o.label}
            />
          ))}
          <Typography variant="subtitle2" sx={{ mt: 3 }}>Trust this device</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            After entering a code, people can tick "Trust this device" to skip the code there for a fixed period. They
            still need their password. Trust ends early when the password changes or two-step sign-in is reset, and
            each person (or an admin, from Users) can forget their trusted devices.
          </Typography>
          <FormControlLabel
            control={<Switch id="mfa-trust-enabled" checked={!!trust.trustDevicesEnabled}
              onChange={e => setTrust(t => ({ ...t, trustDevicesEnabled: e.target.checked }))} />}
            label="Offer “Trust this device”"
          />
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {TRUST_FIELDS.map(f => (
              <Grid item xs={12} md={6} key={f.key}>
                <TextField
                  id={`mfa-${f.key}`}
                  fullWidth type="number" label={f.label}
                  value={trust[f.key]}
                  disabled={!trust.trustDevicesEnabled}
                  onChange={e => setTrust(t => ({ ...t, [f.key]: e.target.value }))}
                  inputProps={{ min: f.min, max: f.max }}
                  helperText={`${f.min} to ${f.max}`}
                />
              </Grid>
            ))}
          </Grid>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Switch id="mfa-new-device-emails" checked={!!trust.newDeviceEmails}
              onChange={e => setTrust(t => ({ ...t, newDeviceEmails: e.target.checked }))} />}
            label="Email staff, and anyone with two-step sign-in on, when they sign in from a new device"
          />
        </CardContent>
      </Card>
      {invalid && <Alert severity="warning" sx={{ mb: 2 }}>{invalid.label} must be between {invalid.min} and {invalid.max}.</Alert>}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={save} disabled={saving || !!invalid}>{saving ? 'Saving…' : 'Save settings'}</Button>
        <Button variant="outlined" onClick={() => { setSettings(DEFAULTS); setTrust(TRUST_DEFAULTS); }} disabled={saving}>Reset to defaults</Button>
      </Box>
    </Box>
  );
};

export default SessionSecuritySettings;
